import { currentConfig } from "./appConfig";
import { getEnv } from "./env";

/**
 * Platform Experience — which product experience to present, independent of
 * the platform the code is actually running on.
 *
 * - "app": the native-app experience. Always used on native; on web it makes
 *   the site behave as an analog of the app (the historical web behavior,
 *   primarily for local development).
 * - "web": the web-hosted experience, optimized for converting visitors into
 *   app installs (e.g. onboarding skips notifications and gates on download).
 *
 * Capability checks (push permissions, live updates, etc.) must keep using
 * `Capacitor.isNativePlatform()` — this value only selects the product flow.
 */
export type PlatformExperience = "app" | "web";

/** Query param that forces an experience, e.g. `?experience=app`. */
export const PLATFORM_EXPERIENCE_QUERY_PARAM = "experience";

const STORAGE_KEY = "platformExperience";

function asExperience(
  value: string | null | undefined
): PlatformExperience | undefined {
  return value === "app" || value === "web" ? value : undefined;
}

/**
 * Capture an `?experience=` override from the current URL into sessionStorage.
 * `?experience=app|web` caches that value; any other value (e.g.
 * `?experience=clear`) removes the cached override. Returns the captured
 * experience, if any.
 */
function captureQueryOverride(): PlatformExperience | undefined {
  const queryValue = new URLSearchParams(window.location.search).get(
    PLATFORM_EXPERIENCE_QUERY_PARAM
  );
  const fromQuery = asExperience(queryValue);
  if (fromQuery) {
    sessionStorage.setItem(STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  if (queryValue) {
    // e.g. ?experience=clear — drop the cached override
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return undefined;
}

// Capture `?experience=` on ANY page load (same pattern as the `?test=`
// group override in useAuthStore) so landing anywhere in the app with the
// param stores the override — not just pages that resolve the experience.
if (typeof window !== "undefined") {
  captureQueryOverride();
}

/**
 * Resolve the platform experience. Native is always "app"; on web the
 * override resolution order is:
 *  1. `?experience=app|web` query param — captured to sessionStorage on any
 *     page load (module init above) and re-checked here, so it survives
 *     navigation and reloads within the same tab. Any other value (e.g.
 *     `?experience=clear`) removes the cached override.
 *  2. sessionStorage override cached for the current tab session
 *  3. `VITE_PLATFORM_EXPERIENCE` env value (set to "app" in `.env.local` to
 *     keep local web dev behaving like the app)
 *  4. default: "web"
 */
export function getPlatformExperience(): PlatformExperience {
  if (currentConfig().isNativePlatform) return "app";

  const fromQuery = captureQueryOverride();
  if (fromQuery) {
    return fromQuery;
  }

  const fromStorage = asExperience(sessionStorage.getItem(STORAGE_KEY));
  if (fromStorage) return fromStorage;

  const fromEnv = asExperience(getEnv("PLATFORM_EXPERIENCE"));
  if (fromEnv) return fromEnv;

  return "web";
}

export function isWebExperience(): boolean {
  return getPlatformExperience() === "web";
}

export function isAppExperience(): boolean {
  return getPlatformExperience() === "app";
}

/** Set (or clear with `null`) the cached experience override. */
export function setPlatformExperience(
  experience: PlatformExperience | null
): void {
  if (experience) {
    sessionStorage.setItem(STORAGE_KEY, experience);
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * The cached experience override (set via `?experience=` or
 * `setPlatformExperience`), or null when resolving from env/default.
 */
export function getPlatformExperienceOverride(): PlatformExperience | null {
  return asExperience(sessionStorage.getItem(STORAGE_KEY)) ?? null;
}
